import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { PaginatedResponse } from "@/shared/http";

import { MetricRecord, QueryMetricsRequest } from "../MetricRecord";

import { hashMetricsQueryKey, metricsQueryKeys } from "./metricsQueryKeys";

/**
 * Single-page raw query for stored metric values whose session time falls in a
 * range. For full-list consumption, prefer `useInfiniteQueryMetrics` or
 * `useAllQueriedMetrics`.
 */
export function useQueryMetrics(
  request: QueryMetricsRequest | undefined,
  orgId: string | undefined,
) {
  const { metricsService } = useDomainServices();

  return useQuery<PaginatedResponse<MetricRecord>, Error>({
    queryKey: metricsQueryKeys.queriesPage(request, orgId),
    queryKeyHashFn: hashMetricsQueryKey,
    queryFn: ({ signal }) => {
      if (request === undefined) {
        return Promise.reject(new Error("Missing query request"));
      }
      return metricsService.queryMetrics(request, {
        abortSignal: signal,
        orgId,
      });
    },
    enabled: request !== undefined && orgId !== undefined,
    staleTime: 60_000,
  });
}

export function useInfiniteQueryMetrics(
  request: QueryMetricsRequest | undefined,
  orgId: string | undefined,
) {
  const { metricsService } = useDomainServices();

  return useInfiniteQuery<PaginatedResponse<MetricRecord>, Error>({
    queryKey: metricsQueryKeys.queriesInfinite(request, orgId),
    queryKeyHashFn: hashMetricsQueryKey,
    queryFn: ({ signal, pageParam }) => {
      if (request === undefined) {
        return Promise.reject(new Error("Missing query request"));
      }
      return metricsService.queryMetrics(request, {
        abortSignal: signal,
        orgId,
        pageToken: pageParam as string | undefined,
      });
    },
    initialPageParam: undefined,
    getNextPageParam: (last) => last.next_token ?? undefined,
    enabled: request !== undefined && orgId !== undefined,
    staleTime: 60_000,
  });
}

/**
 * Auto-paginate to exhaustion and return a flat list. Use only when the caller
 * is sure the result set fits in memory (e.g., dashboard tile with a tight
 * time window).
 */
export function useAllQueriedMetrics(
  request: QueryMetricsRequest | undefined,
  orgId: string | undefined,
) {
  const { metricsService } = useDomainServices();

  return useQuery<MetricRecord[], Error>({
    queryKey: metricsQueryKeys.queriesAll(request, orgId),
    queryKeyHashFn: hashMetricsQueryKey,
    queryFn: ({ signal }) => {
      if (request === undefined) {
        return Promise.reject(new Error("Missing query request"));
      }
      return metricsService.queryAllMetrics(request, {
        abortSignal: signal,
        orgId,
      });
    },
    enabled: request !== undefined && orgId !== undefined,
    staleTime: 60_000,
  });
}
